import * as React from 'react';

type AutoFocusHandler = (event: Event) => void;

/**
 * Radix returns focus only to its own Trigger. `capture` runs from
 * ReturnFocusCapture because this hook's caller stays mounted while closed.
 */
export function useReturnFocus(consumerHandler?: AutoFocusHandler) {
  const openerRef = React.useRef<HTMLElement | null>(null);

  const capture = React.useCallback(() => {
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }, []);

  const onCloseAutoFocus = React.useCallback(
    (event: Event) => {
      consumerHandler?.(event);
      if (event.defaultPrevented) return;
      const opener = openerRef.current;
      if (opener && opener.isConnected) {
        event.preventDefault();
        opener.focus();
      }
    },
    [consumerHandler],
  );

  return { capture, onCloseAutoFocus };
}

/**
 * First child of the overlay content: its layout effect runs on each mount of
 * the content, before autoFocus, child effects or Radix's FocusScope move focus.
 * Relies on the content unmounting on close: with forceMount, or a reopen during
 * the exit animation, the earlier capture stays (#1620).
 */
export function ReturnFocusCapture({ capture }: { capture: () => void }) {
  const capturedRef = React.useRef(false);
  React.useLayoutEffect(() => {
    // StrictMode replays this after focus has already moved inside.
    if (capturedRef.current) return;
    capturedRef.current = true;
    capture();
  }, [capture]);
  return null;
}
